import { useCallback, useEffect, useRef, useState } from "react";
import {
	cancelAsyncReport,
	downloadAsyncReportResult,
	getAsyncReportProgress,
} from "services/ReportService";

type JobProgressStatus = "loading" | "success" | "error" | "canceled";

export interface JobProgressState {
	progress: number;
	status: JobProgressStatus;
	statusMessage: string;
	etaSeconds?: number | null;
	isActive: boolean;
}

const POLL_INTERVAL_MS = 1000;

const INITIAL_STATE: JobProgressState = {
	progress: 0,
	status: "loading",
	statusMessage: "Aguardando início...",
	etaSeconds: null,
	isActive: false,
};

const isTerminal = (s: string) =>
	s === "Completed" || s === "Failed" || s === "Canceled";

const mapStatus = (s: string): JobProgressStatus => {
	if (s === "Completed") return "success";
	if (s === "Failed") return "error";
	if (s === "Canceled") return "canceled";
	return "loading";
};

const sanitizeFilename = (name: string): string =>
	name
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/[^a-zA-Z0-9]/g, "_")
		.replace(/_+/g, "_")
		.replace(/^_|_$/g, "")
		.toLowerCase();

const todayStr = (): string => {
	const d = new Date();
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
};

const triggerDownload = (blob: Blob, jobId: number, reportName?: string | null): void => {
	const ext = blob.type.includes("pdf")
		? ".pdf"
		: blob.type.includes("spreadsheet")
			? ".xlsx"
			: blob.type.includes("csv")
				? ".csv"
				: ".pdf";
	const base = reportName ? sanitizeFilename(reportName) : `relatorio_${jobId}`;
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", `${base}_${todayStr()}${ext}`);
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

export const useJobProgress = () => {
	const [state, setState] = useState<JobProgressState>(INITIAL_STATE);

	const jobIdRef = useRef<number | null>(null);
	const reportNameRef = useRef<string | null>(null);
	const onNoDataRef = useRef<((message: string) => void) | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const stopPolling = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		abortRef.current?.abort();
		abortRef.current = null;
	}, []);

	const poll = useCallback(async () => {
		const jobId = jobIdRef.current;
		if (jobId === null) return;

		const abort = new AbortController();
		abortRef.current = abort;

		try {
			const data = await getAsyncReportProgress(jobId, {
				signal: abort.signal,
			});

			if (jobIdRef.current !== jobId || abort.signal.aborted) return;

			if (isTerminal(data.status)) {
				stopPolling();

				const hasFile = data.status === "Completed" && !!data.resultPath;
				const finalMessage =
					data.status === "Failed"
						? (data.errorMessage ?? "Erro ao gerar relatório")
						: (data.message ?? (hasFile ? "Concluído" : "Nenhum registro encontrado."));

				if (data.status === "Completed" && !hasFile) {
					setState({ ...INITIAL_STATE });
					onNoDataRef.current?.(finalMessage);
					return;
				}

				setState({
					progress: data.status === "Completed" ? 100 : data.progress,
					status: mapStatus(data.status),
					statusMessage: finalMessage,
					etaSeconds: null,
					isActive: false,
				});

				if (hasFile) {
					try {
						const blob = await downloadAsyncReportResult(jobId);
						triggerDownload(blob, jobId, reportNameRef.current);
					} catch {
						// download failed silently — user can retry via GET /jobs/{id}/download
					}
				}
				return;
			}

			setState((prev) => ({
				...prev,
				progress: data.progress,
				status: "loading",
				statusMessage: data.message ?? prev.statusMessage,
				etaSeconds: data.etaSeconds ?? null,
			}));
		} catch {
			// AbortError ou falha de rede — não atualiza estado
		}
	}, [stopPolling]);

	const start = useCallback(
		(jobId: number, reportName?: string | null, onNoData?: (message: string) => void) => {
			stopPolling();
			jobIdRef.current = jobId;
			reportNameRef.current = reportName ?? null;
			onNoDataRef.current = onNoData ?? null;

			setState({
				...INITIAL_STATE,
				isActive: true,
				statusMessage: "Iniciando geração do relatório...",
			});

			void poll();
			intervalRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
		},
		[stopPolling, poll],
	);

	const cancel = useCallback(async () => {
		const jobId = jobIdRef.current;
		if (jobId === null) return;

		stopPolling();

		try {
			await cancelAsyncReport(jobId);
		} catch {
			// ignora erro de cancelamento
		}

		setState((prev) => ({
			...prev,
			status: "canceled",
			statusMessage: "Cancelado pelo usuário",
			etaSeconds: null,
			isActive: false,
		}));
	}, [stopPolling]);

	const reset = useCallback(() => {
		stopPolling();
		jobIdRef.current = null;
		setState(INITIAL_STATE);
	}, [stopPolling]);

	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, [stopPolling]);

	return {
		...state,
		start,
		cancel,
		reset,
	};
};

export default useJobProgress;
